(function () {
  'use strict';

  /* ---------------------------------------------------------------------
   * Theme toggle
   * Initial data-theme is already set by the inline head script in
   * theme.liquid (reads localStorage / prefers-color-scheme) — these two
   * helpers only handle switching after the page has loaded.
   * ------------------------------------------------------------------- */
  /* Locale-aware UI strings (window.ET_STRINGS from theme.liquid); English
     fallbacks keep the file safe if the bridge is missing. */
  var ET_T = (typeof window !== 'undefined' && window.ET_STRINGS) || {};

  window.etSetTheme = function (t) {
    document.documentElement.setAttribute('data-theme', t);
    try {
      localStorage.setItem('et-theme', t);
    } catch (e) {}
    // Starfield dots are theme-colored — repaint immediately so a toggle
    // doesn't leave stale-colored dots (matters for the reduced-motion
    // static render; the rAF loop picks the new color up on its own).
    starfields.forEach(function (s) {
      if (s.redraw) s.redraw();
    });
  };

  window.etToggleTheme = function () {
    var current = document.documentElement.getAttribute('data-theme');
    window.etSetTheme(current === 'dark' ? 'light' : 'dark');
  };

  /* ---------------------------------------------------------------------
   * Reveal-on-scroll
   * ------------------------------------------------------------------- */
  var prefersReducedMotion = false;
  try {
    prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch (e) {}

  var revealObserver = null;

  function observeReveal() {
    var els = document.querySelectorAll('.reveal');
    if (!els.length) return;

    if (prefersReducedMotion || !('IntersectionObserver' in window)) {
      els.forEach(function (el) {
        el.classList.add('in');
        countUpWithin(el);
      });
      return;
    }

    if (revealObserver) revealObserver.disconnect();

    revealObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('in');
            countUpWithin(entry.target);
            revealObserver.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12 }
    );

    els.forEach(function (el) {
      revealObserver.observe(el);
    });
  }

  /* ---------------------------------------------------------------------
   * FAQ accordion — idempotent wiring so it's safe to call again after
   * Shopify re-renders a section in the theme editor. Supports both the
   * current live markup (`.faq-item`/`.faq-q`/`.faq-a`) and the Premium
   * design's own class names (`.fitem`/`.fq`/`.fa`, Task 21).
   * ------------------------------------------------------------------- */
  function wireFaq() {
    document.querySelectorAll('.faq-item, .fitem').forEach(function (item) {
      if (item.getAttribute('data-et-faq-wired') === '1') return;

      var q = item.querySelector('.faq-q, .fq');
      var a = item.querySelector('.faq-a, .fa');
      if (!q || !a) return;

      item.setAttribute('data-et-faq-wired', '1');

      if (item.classList.contains('open')) {
        a.style.maxHeight = a.scrollHeight + 'px';
      }
      q.setAttribute('aria-expanded', item.classList.contains('open') ? 'true' : 'false');

      function toggle() {
        var isOpen = item.classList.toggle('open');
        a.style.maxHeight = isOpen ? a.scrollHeight + 'px' : '0';
        q.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      }

      q.addEventListener('click', toggle);
      q.addEventListener('keydown', function (ev) {
        if (ev.key === 'Enter' || ev.key === ' ') {
          ev.preventDefault();
          toggle();
        }
      });
    });
  }

  /* ---------------------------------------------------------------------
   * Starfield — lightweight drifting-dots canvas for the hero. Targets
   * `canvas.et-stars` (Task 21's future markup) and the current reference's
   * `#stars` id, whichever is present. Respects prefers-reduced-motion
   * (static dots, no rAF loop) and pauses the loop while the tab is hidden.
   * ------------------------------------------------------------------- */
  var starfields = [];

  function initStarfield(canvas) {
    if (!canvas || canvas.getAttribute('data-et-stars-wired') === '1') return;
    canvas.setAttribute('data-et-stars-wired', '1');

    var ctx = canvas.getContext('2d');
    if (!ctx) return;

    var DOT_COUNT = 90;
    var dots = [];
    var rafId = null;
    var width = 0;
    var height = 0;

    function resize() {
      var rect = canvas.parentElement ? canvas.parentElement.getBoundingClientRect() : canvas.getBoundingClientRect();
      width = Math.max(1, Math.round(rect.width || canvas.clientWidth || 1));
      height = Math.max(1, Math.round(rect.height || canvas.clientHeight || 1));
      var dpr = window.devicePixelRatio || 1;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }

    function makeDot() {
      return {
        x: Math.random() * width,
        y: Math.random() * height,
        r: 0.5 + Math.random() * 1,
        o: 0.15 + Math.random() * 0.55,
        speed: 0.06 + Math.random() * 0.18,
      };
    }

    function seed() {
      dots = [];
      for (var i = 0; i < DOT_COUNT; i++) dots.push(makeDot());
    }

    // White dots vanish on the light theme — use deep navy there instead.
    function starRGB() {
      return document.documentElement.getAttribute('data-theme') === 'dark' ? '255,255,255' : '13,52,80';
    }

    function drawStatic() {
      var rgb = starRGB();
      ctx.clearRect(0, 0, width, height);
      dots.forEach(function (d) {
        ctx.beginPath();
        ctx.fillStyle = 'rgba(' + rgb + ',' + d.o + ')';
        ctx.arc(d.x, d.y, d.r, 0, Math.PI * 2);
        ctx.fill();
      });
    }

    function tick() {
      var rgb = starRGB();
      ctx.clearRect(0, 0, width, height);
      dots.forEach(function (d) {
        d.y -= d.speed;
        if (d.y < -2) {
          d.y = height + 2;
          d.x = Math.random() * width;
        }
        ctx.beginPath();
        ctx.fillStyle = 'rgba(' + rgb + ',' + d.o + ')';
        ctx.arc(d.x, d.y, d.r, 0, Math.PI * 2);
        ctx.fill();
      });
      rafId = window.requestAnimationFrame(tick);
    }

    function start() {
      if (rafId || prefersReducedMotion) return;
      rafId = window.requestAnimationFrame(tick);
    }

    function stop() {
      if (rafId) {
        window.cancelAnimationFrame(rafId);
        rafId = null;
      }
    }

    resize();
    seed();

    if (prefersReducedMotion) {
      drawStatic();
    } else {
      start();
    }

    window.addEventListener('resize', function () {
      resize();
      seed();
      if (prefersReducedMotion) drawStatic();
    });

    document.addEventListener('visibilitychange', function () {
      if (document.hidden) {
        stop();
      } else {
        start();
      }
    });

    starfields.push({ canvas: canvas, start: start, stop: stop, redraw: drawStatic });
  }

  function initStarfields() {
    document.querySelectorAll('canvas.et-stars, #stars').forEach(initStarfield);
  }

  /* ---------------------------------------------------------------------
   * Count-up — animates `[data-count]` numbers from 0 to their target when
   * the containing `.reveal` element gets its `.in` class. Supports
   * `data-prefix`/`data-suffix` and thousands separators (e.g. "1,500+").
   * ------------------------------------------------------------------- */
  function easeOutQuad(t) {
    return 1 - (1 - t) * (1 - t);
  }

  function formatCount(n, decimals) {
    var rounded = decimals ? n.toFixed(decimals) : String(Math.round(n));
    var parts = rounded.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return parts.join('.');
  }

  function runCountUp(el) {
    if (el.getAttribute('data-et-count-wired') === '1') return;
    el.setAttribute('data-et-count-wired', '1');

    var target = parseFloat(el.getAttribute('data-count'));
    if (isNaN(target)) return;
    var prefix = el.getAttribute('data-prefix') || '';
    var suffix = el.getAttribute('data-suffix') || '';
    var raw = el.getAttribute('data-count') || '';
    var decimals = raw.indexOf('.') > -1 ? raw.split('.')[1].length : 0;

    if (prefersReducedMotion) {
      el.textContent = prefix + formatCount(target, decimals) + suffix;
      return;
    }

    var duration = 1400;
    var startTime = null;

    function frame(ts) {
      if (startTime === null) startTime = ts;
      var progress = Math.min(1, (ts - startTime) / duration);
      var eased = easeOutQuad(progress);
      el.textContent = prefix + formatCount(target * eased, decimals) + suffix;
      if (progress < 1) {
        window.requestAnimationFrame(frame);
      } else {
        el.textContent = prefix + formatCount(target, decimals) + suffix;
      }
    }

    window.requestAnimationFrame(frame);
  }

  function countUpWithin(root) {
    if (!root || !root.querySelectorAll) return;
    root.querySelectorAll('[data-count]').forEach(runCountUp);
  }

  /* ---------------------------------------------------------------------
   * Header scrolled state — toggles `.et-scrolled` on `.header-wrapper`
   * once the page scrolls past a small threshold (glass/blur treatment).
   * ------------------------------------------------------------------- */
  function updateHeaderScrolled() {
    var header = document.querySelector('.header-wrapper');
    if (!header) return;
    if (window.scrollY > 8) {
      header.classList.add('et-scrolled');
    } else {
      header.classList.remove('et-scrolled');
    }
  }

  /* ---------------------------------------------------------------------
   * Modals — <dialog class="et-modal"> opened by [data-et-modal="<id>"]
   * triggers. Native ESC handling comes free; backdrop click and the ×
   * button are wired here. Delegated, so section re-renders stay safe.
   * ------------------------------------------------------------------- */
  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('[data-et-modal]');
    if (trigger) {
      var dlg = document.getElementById(trigger.getAttribute('data-et-modal'));
      if (dlg && dlg.showModal) dlg.showModal();
      return;
    }
    var closeBtn = e.target.closest('[data-et-modal-close]');
    if (closeBtn) {
      var openDlg = closeBtn.closest('dialog');
      if (openDlg) openDlg.close();
      return;
    }
    // click on the backdrop = click lands on the <dialog> element itself
    if (e.target instanceof HTMLElement && e.target.matches('dialog.et-modal')) {
      var r = e.target.getBoundingClientRect();
      var inside = e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom;
      if (!inside) e.target.close();
    }
  });

  /* ---------------------------------------------------------------------
   * Newsletter subscribe → Pipedrive lead (via the forms endpoint).
   * Falls through to the native Shopify customer form when no endpoint
   * is configured.
   * ------------------------------------------------------------------- */
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!(form instanceof HTMLFormElement) || !form.querySelector('.news')) return;
    var endpoint = window.ET_FORMS_ENDPOINT;
    if (!endpoint) return;

    e.preventDefault();
    var emailField = form.querySelector('[name="contact[email]"]');
    var msg = form.querySelector('.js-news-msg');
    var btn = form.querySelector('button[type="submit"]');
    var email = emailField ? (emailField.value || '').trim() : '';

    function setMsg(kind, text) {
      if (!msg) return;
      msg.className = 'vf-msg js-news-msg show ' + kind;
      msg.textContent = text;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setMsg('err', ET_T.invalidEmail || 'Please enter a valid email address.');
      return;
    }

    var fd = new FormData();
    fd.append('email', email);
    fd.append('kind', 'subscribe');
    fd.append('page', window.location.pathname);

    if (btn) btn.disabled = true;
    fetch(endpoint, { method: 'POST', body: fd })
      .then(function (res) { return res.json().catch(function () { return { ok: false }; }); })
      .then(function (data) {
        if (data.ok) {
          form.reset();
          setMsg('ok', ET_T.newsSuccess || "Thanks — you're on the list.");
        } else {
          setMsg('err', data.error || ET_T.newsError || 'Subscription failed — please retry.');
        }
      })
      .catch(function () { setMsg('err', ET_T.errorNetwork || 'Network problem — please retry.'); })
      .finally(function () { if (btn) btn.disabled = false; });
  });

  /* ---------------------------------------------------------------------
   * Valuation form validation
   * Delegated so it keeps working after Shopify re-renders a section.
   * Forms are real Shopify contact forms — on valid input we let the
   * native submit proceed (no preventDefault) so the POST actually
   * reaches Shopify.
   * ------------------------------------------------------------------- */
  document.addEventListener('submit', function (e) {
    var form = e.target.closest('form.vform-card');
    if (!form) return;

    var emailField = form.querySelector('[name="contact[email]"], [name="email"]');
    var descField = form.querySelector('[name="contact[body]"], [name="description"]');
    var filesField = form.querySelector('[name="files"]');
    var msg = form.querySelector('.js-vf-msg');

    var email = emailField ? (emailField.value || '').trim() : '';
    var desc = descField ? (descField.value || '').trim() : '';
    var files = filesField ? filesField.files : null;

    function setMsg(kind, text) {
      if (!msg) return;
      msg.className = 'vf-msg js-vf-msg show ' + kind;
      msg.textContent = text;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      e.preventDefault();
      setMsg('err', ET_T.invalidEmail || 'Please enter a valid email address.');
      if (emailField) emailField.focus();
      return;
    }

    if (!desc && (!files || files.length === 0)) {
      e.preventDefault();
      setMsg('err', ET_T.needDetails || 'Add a short description or attach a file so we know what to value.');
      return;
    }

    var submitBtn = form.querySelector('button[type="submit"]');
    var submitLabel = submitBtn ? submitBtn.textContent : '';
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.textContent = ET_T.sending || 'Sending…';
    }

    /* Custom endpoint (Cloudflare Worker → Pipedrive, window.ET_FORMS_ENDPOINT
       set in theme.liquid from settings.et_forms_endpoint). When blank, the
       native Shopify contact POST proceeds as before — no fetch, no
       preventDefault. */
    var endpoint = window.ET_FORMS_ENDPOINT;
    if (!endpoint) return;

    e.preventDefault();

    fetch(endpoint, { method: 'POST', body: new FormData(form) })
      .then(function (res) {
        return res.json().catch(function () { return { ok: false }; });
      })
      .then(function (data) {
        if (data.ok) {
          form.reset();
          setMsg('ok', ET_T.formSuccess || "Thanks — we'll come back with a demand-backed view shortly.");
        } else {
          setMsg('err', data.error || ET_T.errorGeneric || 'Something went wrong — please email us directly.');
        }
      })
      .catch(function () {
        setMsg('err', ET_T.errorNetwork || 'Network problem — please retry, or email us directly.');
      })
      .finally(function () {
        if (submitBtn) {
          submitBtn.disabled = false;
          submitBtn.textContent = submitLabel;
        }
      });
  });

  /* ---------------------------------------------------------------------
   * Init
   * ------------------------------------------------------------------- */
  var scrolledListenerWired = false;

  function init() {
    observeReveal();
    wireFaq();
    initStarfields();

    if (!scrolledListenerWired) {
      scrolledListenerWired = true;
      window.addEventListener('scroll', updateHeaderScrolled, { passive: true });
    }
    updateHeaderScrolled();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
